import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface TransportFeesProps {
  domain: string;
}

interface FeeStructure {
  id: string;
  routeNumber: string;
  routeName: string;
  monthlyFee: number;
  quarterlyFee: number;
  annualFee: number;
  distance: string;
}

interface FeePayment {
  id: string;
  studentId: string;
  studentName: string;
  routeNumber: string;
  amount: number;
  paymentDate: string;
  paymentMode: string;
  receiptNumber: string;
  period: string;
  status: 'Paid' | 'Pending' | 'Overdue';
}

const TransportFees: React.FC<TransportFeesProps> = ({ domain }) => {
  const [activeView, setActiveView] = useState<'structure' | 'payments'>('structure');
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([]);
  const [feePayments, setFeePayments] = useState<FeePayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStructureForm, setShowStructureForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingStructure, setEditingStructure] = useState<FeeStructure | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [structureFormData, setStructureFormData] = useState({
    routeNumber: '',
    routeName: '',
    monthlyFee: '',
    quarterlyFee: '',
    annualFee: '',
    distance: '',
  });

  const [paymentFormData, setPaymentFormData] = useState({
    studentId: '',
    studentName: '',
    routeNumber: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'Cash',
    receiptNumber: '',
    period: 'Monthly',
    status: 'Paid' as 'Paid' | 'Pending' | 'Overdue',
  });

  useEffect(() => {
    fetchFeeStructures();
    fetchFeePayments();
  }, [domain]);

  const fetchFeeStructures = async () => {
    try {
      setLoading(true);
      const structuresRef = collection(db, `school-users/${domain}/transport-fee-structure`);
      const snapshot = await getDocs(structuresRef);
      const structures: FeeStructure[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FeeStructure));
      setFeeStructures(structures);
    } catch (error) {
      console.error('Error fetching fee structures:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeePayments = async () => {
    try {
      const paymentsRef = collection(db, `school-users/${domain}/transport-fee-payments`);
      const snapshot = await getDocs(paymentsRef);
      const payments: FeePayment[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FeePayment));
      setFeePayments(payments);
    } catch (error) {
      console.error('Error fetching fee payments:', error);
    }
  };

  const handleStructureInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStructureFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePaymentInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPaymentFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStructureSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!structureFormData.routeNumber || !structureFormData.monthlyFee) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const structureData = {
        ...structureFormData,
        monthlyFee: Number(structureFormData.monthlyFee),
        quarterlyFee: Number(structureFormData.quarterlyFee),
        annualFee: Number(structureFormData.annualFee),
      };

      if (editingStructure) {
        const structureRef = doc(db, `school-users/${domain}/transport-fee-structure`, editingStructure.id);
        await updateDoc(structureRef, structureData);
        alert('Fee structure updated successfully!');
      } else {
        await addDoc(collection(db, `school-users/${domain}/transport-fee-structure`), structureData);
        alert('Fee structure added successfully!');
      }

      resetStructureForm();
      fetchFeeStructures();
    } catch (error) {
      console.error('Error saving fee structure:', error);
      alert('Failed to save fee structure');
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!paymentFormData.studentId || !paymentFormData.studentName || !paymentFormData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const paymentData = {
        ...paymentFormData,
        amount: Number(paymentFormData.amount),
      };

      await addDoc(collection(db, `school-users/${domain}/transport-fee-payments`), paymentData);
      alert('Payment recorded successfully!');

      resetPaymentForm();
      fetchFeePayments();
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('Failed to record payment');
    }
  };

  const handleEditStructure = (structure: FeeStructure) => {
    setEditingStructure(structure);
    setStructureFormData({
      routeNumber: structure.routeNumber,
      routeName: structure.routeName,
      monthlyFee: structure.monthlyFee.toString(),
      quarterlyFee: structure.quarterlyFee.toString(),
      annualFee: structure.annualFee.toString(),
      distance: structure.distance,
    });
    setShowStructureForm(true);
  };

  const resetStructureForm = () => {
    setStructureFormData({
      routeNumber: '',
      routeName: '',
      monthlyFee: '',
      quarterlyFee: '',
      annualFee: '',
      distance: '',
    });
    setEditingStructure(null);
    setShowStructureForm(false);
  };

  const resetPaymentForm = () => {
    setPaymentFormData({
      studentId: '',
      studentName: '',
      routeNumber: '',
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: 'Cash',
      receiptNumber: '',
      period: 'Monthly',
      status: 'Paid',
    });
    setShowPaymentForm(false);
  };

  const filteredStructures = feeStructures.filter(structure =>
    structure.routeNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    structure.routeName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPayments = feePayments.filter(payment =>
    payment.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.receiptNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalCollected = feePayments
    .filter(p => p.status === 'Paid')
    .reduce((sum, p) => sum + p.amount, 0);

  const totalPending = feePayments
    .filter(p => p.status === 'Pending' || p.status === 'Overdue')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="bi bi-currency-dollar me-2"></i>Transport Fees Management
        </h5>
        <div className="btn-group" role="group">
          <button
            className={`btn ${activeView === 'structure' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveView('structure')}
          >
            <i className="bi bi-list-ul me-1"></i>Fee Structure
          </button>
          <button
            className={`btn ${activeView === 'payments' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveView('payments')}
          >
            <i className="bi bi-cash-stack me-1"></i>Payments
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-1">Total Collected</h6>
              <h4 className="mb-0 text-success">₹ {totalCollected.toLocaleString('en-IN')}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-1">Pending Amount</h6>
              <h4 className="mb-0 text-warning">₹ {totalPending.toLocaleString('en-IN')}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-1">Total Payments</h6>
              <h4 className="mb-0">{feePayments.length}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-1">Routes</h6>
              <h4 className="mb-0">{feeStructures.length}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Fee Structure View */}
      {activeView === 'structure' && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Fee Structure by Route</h6>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowStructureForm(!showStructureForm)}
            >
              <i className="bi bi-plus-circle me-1"></i>
              {showStructureForm ? 'Cancel' : 'Add Structure'}
            </button>
          </div>

          {/* Structure Form */}
          {showStructureForm && (
            <div className="card mb-4 shadow-sm">
              <div className="card-body">
                <h6 className="card-title mb-3">
                  {editingStructure ? 'Edit Fee Structure' : 'Add Fee Structure'}
                </h6>
                <form onSubmit={handleStructureSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Route Number *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="routeNumber"
                        value={structureFormData.routeNumber}
                        onChange={handleStructureInputChange}
                        placeholder="e.g., R-01"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Route Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="routeName"
                        value={structureFormData.routeName}
                        onChange={handleStructureInputChange}
                        placeholder="e.g., North Delhi Route"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Monthly Fee (₹) *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="monthlyFee"
                        value={structureFormData.monthlyFee}
                        onChange={handleStructureInputChange}
                        placeholder="e.g., 1500"
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Quarterly Fee (₹)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="quarterlyFee"
                        value={structureFormData.quarterlyFee}
                        onChange={handleStructureInputChange}
                        placeholder="e.g., 4200"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Annual Fee (₹)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="annualFee"
                        value={structureFormData.annualFee}
                        onChange={handleStructureInputChange}
                        placeholder="e.g., 15000"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Distance (km)</label>
                      <input
                        type="text"
                        className="form-control"
                        name="distance"
                        value={structureFormData.distance}
                        onChange={handleStructureInputChange}
                        placeholder="e.g., 15 km"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button type="submit" className="btn btn-primary me-2">
                      <i className="bi bi-check-circle me-1"></i>
                      {editingStructure ? 'Update' : 'Add'} Structure
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={resetStructureForm}>
                      <i className="bi bi-x-circle me-1"></i>Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="card mb-3 shadow-sm">
            <div className="card-body">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by route number or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Structure Table */}
          <div className="card shadow-sm">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : filteredStructures.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-currency-dollar fs-1 d-block mb-3"></i>
                  <p>No fee structures found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Route Number</th>
                        <th>Route Name</th>
                        <th>Distance</th>
                        <th>Monthly Fee</th>
                        <th>Quarterly Fee</th>
                        <th>Annual Fee</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStructures.map((structure) => (
                        <tr key={structure.id}>
                          <td className="fw-semibold">{structure.routeNumber}</td>
                          <td>{structure.routeName}</td>
                          <td>{structure.distance || 'N/A'}</td>
                          <td>₹ {structure.monthlyFee.toLocaleString('en-IN')}</td>
                          <td>₹ {structure.quarterlyFee.toLocaleString('en-IN')}</td>
                          <td>₹ {structure.annualFee.toLocaleString('en-IN')}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEditStructure(structure)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Payments View */}
      {activeView === 'payments' && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Fee Payments</h6>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowPaymentForm(!showPaymentForm)}
            >
              <i className="bi bi-plus-circle me-1"></i>
              {showPaymentForm ? 'Cancel' : 'Record Payment'}
            </button>
          </div>

          {/* Payment Form */}
          {showPaymentForm && (
            <div className="card mb-4 shadow-sm">
              <div className="card-body">
                <h6 className="card-title mb-3">Record Transport Fee Payment</h6>
                <form onSubmit={handlePaymentSubmit}>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Student ID *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="studentId"
                        value={paymentFormData.studentId}
                        onChange={handlePaymentInputChange}
                        placeholder="e.g., STU-001"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Student Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="studentName"
                        value={paymentFormData.studentName}
                        onChange={handlePaymentInputChange}
                        placeholder="e.g., Rahul Sharma"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Route Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="routeNumber"
                        value={paymentFormData.routeNumber}
                        onChange={handlePaymentInputChange}
                        placeholder="e.g., R-01"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Amount (₹) *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="amount"
                        value={paymentFormData.amount}
                        onChange={handlePaymentInputChange}
                        placeholder="e.g., 1500"
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Payment Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        name="paymentDate"
                        value={paymentFormData.paymentDate}
                        onChange={handlePaymentInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Payment Mode *</label>
                      <select
                        className="form-select"
                        name="paymentMode"
                        value={paymentFormData.paymentMode}
                        onChange={handlePaymentInputChange}
                        required
                      >
                        <option value="Cash">Cash</option>
                        <option value="Online">Online</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Card">Card</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Receipt Number</label>
                      <input
                        type="text"
                        className="form-control"
                        name="receiptNumber"
                        value={paymentFormData.receiptNumber}
                        onChange={handlePaymentInputChange}
                        placeholder="e.g., REC-001"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Period</label>
                      <select
                        className="form-select"
                        name="period"
                        value={paymentFormData.period}
                        onChange={handlePaymentInputChange}
                      >
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                        <option value="Annual">Annual</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        name="status"
                        value={paymentFormData.status}
                        onChange={handlePaymentInputChange}
                      >
                        <option value="Paid">Paid</option>
                        <option value="Pending">Pending</option>
                        <option value="Overdue">Overdue</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button type="submit" className="btn btn-primary me-2">
                      <i className="bi bi-check-circle me-1"></i>Record Payment
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={resetPaymentForm}>
                      <i className="bi bi-x-circle me-1"></i>Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="card mb-3 shadow-sm">
            <div className="card-body">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by student ID, name, or receipt number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Payments Table */}
          <div className="card shadow-sm">
            <div className="card-body">
              {filteredPayments.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-cash-stack fs-1 d-block mb-3"></i>
                  <p>No payment records found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Receipt No.</th>
                        <th>Student ID</th>
                        <th>Student Name</th>
                        <th>Route</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Mode</th>
                        <th>Period</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment) => (
                        <tr key={payment.id}>
                          <td className="fw-semibold">{payment.receiptNumber || 'N/A'}</td>
                          <td>{payment.studentId}</td>
                          <td>{payment.studentName}</td>
                          <td>{payment.routeNumber || 'N/A'}</td>
                          <td className="fw-semibold">₹ {payment.amount.toLocaleString('en-IN')}</td>
                          <td>{payment.paymentDate}</td>
                          <td>{payment.paymentMode}</td>
                          <td>{payment.period}</td>
                          <td>
                            <span className={`badge ${
                              payment.status === 'Paid' ? 'bg-success' :
                              payment.status === 'Pending' ? 'bg-warning' : 'bg-danger'
                            }`}>
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TransportFees;
